import React, { useCallback, useContext, useEffect, useState } from "react";

import ActionPermissionGuard from "shared/components/utils/ActionPermissionGuard";
import PropTypes from "prop-types";
import Button from "shared/components/inputs/mui/Button";
import { Container } from "react-bootstrap";
import Context from "features/allocations/contexts/Context";
import CustomGroupContext from "features/allocations/contexts/CustomGroupContext";
import EmployeeRow from "./Row";
import Header from "./Header";
import Text from "shared/components/data-display/mui/Text";
import {
    Message,
    Actions,
    MessageWrapper,
} from "./Grid.styles";

const Grid = ({
    selection,
    allSelected,
    onEmployeeSelection,
    onEnableRowsSelection,
    onSelectAll,
    rowsLength,
    selectedLength,
    reset,
    onResize,
    _id,
}) => {
    let summaryError = null;

    const {
        employees,
        setEmployees,
        gridParams,
        columnsView,
        groupBy,
        sortBy,
        isLoadingMore,
        setIsLoadingMore,
        openFilters,
    } = useContext(Context);

    const { selectedCustomGroup } = useContext(CustomGroupContext);

    const [emps, setEmps] = useState(employees);

    const gridMessage = (title, message, actions) => {
        return (
            <MessageWrapper>
                {title && (
                    <Message>
                        <Text variant="largeBold">{title}</Text>
                        <Text variant="smallLight">{message}</Text>
                    </Message>
                )}
                {actions && <Actions>{actions.map((item) => item)}</Actions>}
            </MessageWrapper>
        );
    };

    //Render conditions
    const renderNoResults = (
        <ActionPermissionGuard
            code="AC_VIEW_FILTER"
            fallback={gridMessage("No Employees Found")}
        >
            {gridMessage(
                "Allocations",
                <p style={{ width: "290px" }}>
                    To start working with and viewing employee allocations, please apply a filter or load a saved one from
                    the filters panel, select a custom group or select and employee in the search tool on the right side of the header bar.
                </p>
            )}
            <div
                style={{
                    textAlign: "center",
                    padding: "0",
                    display: "grid",
                    justifyContent: "center",
                    alignContent: "center",
                }}
            >
                <Button
                    style={{
                        width: "11rem",
                        height: "2.8rem",
                        alignSelf: "center",
                        backgroundColor: "white",
                        color: "black",
                        border: "1px solid black",
                        fontWeigth: "bold",
                    }}
                    onClick={openFilters}
                >
                    Open Filters
                </Button>
            </div>
        </ActionPermissionGuard>
    );

    const renderErrorResults = gridMessage(
        "Oops! something went wrong fetching the data",
        "Data is not accessible, please try again later"
    );

    const renderEmployees = emps.map((item, index) => {
        return (
            <EmployeeRow
                index={index}
                key={item[0]?.userId ?? index}
                onResize={onResize}
                employees={item}
                columnsView={columnsView}
                groupBy={groupBy}
                params={gridParams}
                selection={selection}
                allSelected={allSelected}
                onEmployeeSelection={onEmployeeSelection}
            />)
    }
    );

    let renderEmployeesResult = null;
    if (emps?.[0] && emps[0].length > 0 && emps[0].length <= 250) {
        renderEmployeesResult = renderEmployees;
    } else if (emps?.[0] && emps[0].length > 250) {
        renderEmployeesResult = gridMessage(
            "You are trying to fetch more data than allowed",
            "Please, apply a new filter"
        );
    } else if (selectedCustomGroup?.Id > 0) {
        renderEmployeesResult = gridMessage(
            "The selected custom group is empty",
            "Please, apply a new filter and then select employees to add to the custom group"
        );
    } else {
        renderEmployeesResult = renderNoResults;
    }

    const renderResult = summaryError ? renderErrorResults : renderEmployeesResult;

    const totalCapacity = (weeks) => {
        let total = 0;
        weeks.forEach((w) => {
            total += w.allocatedHours;
        });
        return total;
    }

    const compareValues = (a, b) => {
        if (typeof a === "string" && typeof b === "string") {
            return a.localeCompare(b, undefined, { sensitivity: "base" });
        }
        if (a > b) return 1;
        if (a < b) return -1;
        return 0;
    };

    const sortCollection = (items, field, order) => {
        const dir = order === "desc" ? 1 : -1;
        if (field === "TBD") {
            const hasTBD = items.some((t) => t["lastName"] === "TBD");
            return hasTBD ? items.sort((a, b) => a["lastName"] === "TBD" ? dir : -dir) : items;
        }
        if (field === "Capacity") {
            return items.sort((a, b) => totalCapacity(a.weeks) > totalCapacity(b.weeks) ? dir : -dir);
        }
        return items.sort((a, b) => compareValues(a[field], b[field]) * dir);
    };

    const onlySortByEmployees = (selectedValue) => {
        (async () => {
            const values = selectedValue.split(',');
            const field = values[0];
            const order = values[1];
            let collection = [...employees];
            if (collection?.[0]) {
                collection[0] = sortCollection(collection[0], field, order);
                setEmployees(collection);
            }
        })().then(() => {
            setIsLoadingMore({
                loading: false,
                page: isLoadingMore.page,
                message: "",
                type: ""
            });
        });
    }

    const onlyGroupByEmployees = (selectedValue) => {
        (async () => {
            let collection = [...employees];
            if (collection?.[0]) {
                const sorted = collection[0].sort((a, b) => a[selectedValue] > b[selectedValue] ? 1 : -1);
                collection[0] = sorted;
                setEmployees(collection);
            }
        })().then(() => {
            setIsLoadingMore({
                loading: false,
                page: isLoadingMore.page,
                message: "",
                type: ""
            });
        });
    }

    const groupAndSortComparator = (a, b, field, order, groupValue) => {
        const groupCmp = compareValues(a[groupValue], b[groupValue]);
        if (groupCmp !== 0) return groupCmp;
        const dir = order === "desc" ? 1 : -1;
        if (field === "Capacity") {
            return totalCapacity(a.weeks) > totalCapacity(b.weeks) ? dir : -dir;
        }
        if (field === "TBD") {
            return a["lastName"] === "TBD" ? dir : -dir;
        }
        return compareValues(a[field], b[field]) * dir;
    };

    const groupAndSortEmployees = (sortValue, groupValue) => {
        (async () => {
            const values = sortValue.split(',');
            const field = values[0];
            const order = values[1];
            let collection = [...employees];
            if (collection?.[0]) {
                const skipSort = field === "TBD" && !collection[0].some((t) => t["lastName"] === "TBD");
                if (!skipSort) {
                    collection[0] = collection[0].sort((a, b) => groupAndSortComparator(a, b, field, order, groupValue));
                }
                setEmployees(collection);
            }
        })().then(() => {
            setIsLoadingMore({
                loading: false,
                page: isLoadingMore.page,
                message: "",
                type: ""
            });
        });
    }

    const groupAndSort = useCallback(() => {
        if (employees && employees.length > 0) {
            if (groupBy != null && sortBy != null) {
                groupAndSortEmployees(sortBy, groupBy);
            } else if (groupBy != null) {
                onlyGroupByEmployees(groupBy);
            } else if (sortBy != null) {
                onlySortByEmployees(sortBy);
            }
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupBy, sortBy]);

    useEffect(() => {
        groupAndSort();
    }, [groupAndSort]);

    useEffect(() => {
        setEmps(employees);
    }, [employees])

    return (
        <Context.Consumer>
                {(_context) => (
                    <>
                        {(!isLoadingMore.loading || (isLoadingMore.loading && isLoadingMore.type === "save")) && emps ? (
                            <Container fluid style={{ paddingTop: "100px", paddingLeft: "0px", paddingRight: "0px" }}>
                                <div id="allocationsColumnsHeader" style={{ pointerEvents: emps.length === 0 ? "none" : "", cursor: emps.length === 0 ? "not-allowed" : "", opacity: emps.length === 0 ? "0.25" : "" }}>
                                    <Header
                                        onEnableRowsSelection={(enable) => onEnableRowsSelection(enable)}
                                        onSelectAll={(event) => onSelectAll(event)}
                                        {
                                        ...
                                        {
                                            rowsLength: rowsLength,
                                            selectedLength: selectedLength,
                                            reset: reset,
                                        }
                                        }
                                    />
                                </div>
                                <div style={{ paddingTop: "50px", minHeight: "calc(200vh - 150px)" }}>{renderResult}</div>
                            </Container>
                        ) : null
                        }
                    </>
                )}
        </Context.Consumer>
    );
};

Grid.propTypes = {
    selection: PropTypes.object,
    allSelected: PropTypes.bool,
    onEmployeeSelection: PropTypes.func,
    onEnableRowsSelection: PropTypes.func,
    onSelectAll: PropTypes.func,
    rowsLength: PropTypes.number,
    selectedLength: PropTypes.number,
    reset: PropTypes.bool,
    onResize: PropTypes.func,
    _id: PropTypes.string,
};

export default Grid;
